import type { TTurnaroundTime } from '@team-milva/lab-models/resources'

/**
 * The canonical display order for turnaround times, used **everywhere** they
 * appear in a list or a dropdown.
 *
 * 1. **Walk-ins first, alphabetically by name.** They are same-day, so they
 *    lead — and among themselves a name is what a user scans for, not a
 *    duration ("Walk-in asbest" before "Walk-in skimmel", regardless of which
 *    takes 2 hours and which takes 3).
 * 2. **Then fixed deadlines, by working days, then cut-off time.** So
 *    "1 dag kl. 12:00" precedes "1 dag kl. 17:00", which precedes
 *    "2 dage kl. 17:00".
 *
 * Sorting by `durationMilliseconds` alone gets (1) and (2) *nearly* right but
 * orders walk-ins by duration rather than name, and sorting by name alone puts
 * "1 dag" ahead of "Walk-in ..." because digits sort before letters. Both had
 * shipped in different places; this is the single definition.
 *
 * See the Turnaround Times feature doc in the brain for the product rule.
 */
export function compareTurnaroundTimes(
  a: TTurnaroundTime,
  b: TTurnaroundTime,
  locale: string
): number {
  const aIsWalkIn = a.type === 'walk-in'
  const bIsWalkIn = b.type === 'walk-in'

  if (aIsWalkIn !== bIsWalkIn) return aIsWalkIn ? -1 : 1

  if (a.type === 'walk-in' && b.type === 'walk-in') {
    return getWalkInName(a, locale).localeCompare(getWalkInName(b, locale))
  }

  if (a.type === 'fixed-deadline' && b.type === 'fixed-deadline') {
    if (a.workingDays !== b.workingDays) return a.workingDays - b.workingDays
    if (a.deadline.hour !== b.deadline.hour)
      return a.deadline.hour - b.deadline.hour
    return a.deadline.minute - b.deadline.minute
  }

  return 0
}

function getWalkInName(
  tat: Extract<TTurnaroundTime, { type: 'walk-in' }>,
  locale: string
): string {
  return tat.name[locale] || tat.name['en'] || Object.values(tat.name)[0] || ''
}

/** `compareTurnaroundTimes` as a copying sort, for the common case. */
export function sortTurnaroundTimes<T extends TTurnaroundTime>(
  tats: T[],
  locale: string
): T[] {
  return [...tats].sort((a, b) => compareTurnaroundTimes(a, b, locale))
}

/**
 * The same order for anything keyed by TAT ID rather than holding the TAT
 * itself — price lines, grid columns, option lists. Unknown IDs sort last, so a
 * dangling reference never silently jumps to the front.
 */
export function compareByTatID(
  aTatID: string,
  bTatID: string,
  allTats: TTurnaroundTime[],
  locale: string
): number {
  const a = allTats.find((x) => x.id === aTatID)
  const b = allTats.find((x) => x.id === bTatID)
  if (!a && !b) return aTatID.localeCompare(bTatID)
  if (!a) return 1
  if (!b) return -1
  return compareTurnaroundTimes(a, b, locale)
}
